import * as SentryBrowser from "@sentry/browser";
import { sanitizeTelemetryEvent, suppressTelemetryBreadcrumb } from "@shared/telemetry-safety";

// Task #812: browser-side error monitoring.
//
// Task #772 added a top-level React error boundary that calls
// `window.Sentry.captureException(...)` to display a Sentry event ID on
// render-time crashes. That code only does anything if the browser SDK is
// actually loaded onto `window.Sentry` — until this module shipped, only
// `@sentry/node` was wired up (server-side) and the boundary's fallback
// page rendered without an "Error ID:" line, leaving users nothing to
// share with support.
//
// Configuration mirrors `server/monitoring.ts` so a single Sentry project
// receives both server- and browser-side events grouped by the same
// `release` tag:
//   - `VITE_SENTRY_DSN`         — required; absence keeps everything a no-op
//   - `VITE_SENTRY_ENVIRONMENT` — falls back to Vite's MODE
//   - `VITE_SENTRY_RELEASE`     — operator override
//   - `VITE_SENTRY_RELEASE_DEFAULT` — baked in by `script/build.ts` from the
//     same derivation logic the server bundle and source-map upload use
//     (Task #775), so a production deploy reports under the release Sentry
//     already received the maps for.

declare global {
  interface Window {
    Sentry?: typeof SentryBrowser;
  }
}

let initialized = false;

export interface BrowserMonitoringConfig {
  dsn?: string;
  environment?: string;
  release?: string;
}

type EnvLike = Record<string, string | undefined>;

export function resolveBrowserMonitoringConfig(
  env: EnvLike = (import.meta as unknown as { env: EnvLike }).env,
): BrowserMonitoringConfig {
  const trim = (v: string | undefined): string | undefined => {
    if (typeof v !== "string") return undefined;
    const t = v.trim();
    return t.length > 0 ? t : undefined;
  };
  return {
    dsn: trim(env.VITE_SENTRY_DSN),
    environment: trim(env.VITE_SENTRY_ENVIRONMENT) ?? trim(env.MODE),
    release:
      trim(env.VITE_SENTRY_RELEASE) ?? trim(env.VITE_SENTRY_RELEASE_DEFAULT),
  };
}

/**
 * Initialize the browser Sentry SDK and expose it on `window.Sentry` so the
 * top-level error boundary (Task #772) can forward render-time crashes and
 * surface a tracking ID. Safe to call more than once. Returns true when the
 * SDK is wired up, false when monitoring is intentionally disabled (no DSN)
 * or initialization fails — in either case the rest of the app keeps
 * loading.
 */
export function initBrowserMonitoring(
  config: BrowserMonitoringConfig = resolveBrowserMonitoringConfig(),
): boolean {
  if (initialized) return true;
  if (!config.dsn) {
    console.log(
      "[monitoring] VITE_SENTRY_DSN not set — browser error monitoring disabled",
    );
    return false;
  }
  try {
    SentryBrowser.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,
      // Exception reports only — no performance traces, matches the server
      // configuration in `server/monitoring.ts`.
      tracesSampleRate: 0,
      // Avoid PII unless callers explicitly attach it to a scope.
      sendDefaultPii: false,
      beforeSend: sanitizeTelemetryEvent,
      beforeBreadcrumb: suppressTelemetryBreadcrumb,
      // Replay is not installed. Keep both sampling rates explicitly off so
      // future SDK changes cannot start recording XHR/fetch payloads.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      integrations: (defaults) => defaults.filter((integration) => integration.name !== "Replay"),
    });
    if (typeof window !== "undefined") {
      window.Sentry = SentryBrowser;
    }
    initialized = true;
    console.log("[monitoring] browser Sentry initialized");
    return true;
  } catch (err) {
    console.error("[monitoring] browser Sentry init failed:", err);
    return false;
  }
}

export function isBrowserMonitoringInitialized(): boolean {
  return initialized;
}

// Test-only hook so unit tests can reset state between cases.
export function __resetBrowserMonitoringForTests(): void {
  initialized = false;
  if (typeof window !== "undefined") {
    delete window.Sentry;
  }
}
